<template>
  <div
    :id="accordionId"
    class="accordion-customizable-fi-panel"
    :class="isOpen ? 'accordion-customizable-fi-panel--active' : ''"
    :data-index="itemIndex"
  >
    <div
      :id="`${accordionId}_button`"
      ref="accordionItemHeader"
      :aria-expanded="isOpen"
      :aria-controls="`${accordionId}_content`"
      class="accordion-customizable-fi-panel__header"
      type="button"
      @click="toggleContent"
    >
      <slot name="headline" />

      <div class="accordion-customizable-fi-panel__icon-wrapper">
        <div
          :class="`accordion-customizable-fi-panel__icon accordion-customizable-fi-panel__icon--${shopType}`"
        >
          <icon-fi
            :rotate="rotate"
            :icon="icon"
            aria-hidden="true"
          />
        </div>
      </div>
    </div>

    <div
      :id="`${accordionId}_content`"
      ref="content"
      :style="{
        gridTemplateRows: isOpen ? '1fr' : '0fr',
      }"
      :label="label"
      :aria-hidden="!isOpen"
      role="region"
      class="accordion-customizable-fi-panel__content"
    >
      <div class="accordion-customizable-fi-panel__content__wrapper">
        <div class="accordion-customizable-fi-panel__card">
          <slot name="content" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, useId } from 'vue';

import IconFi from '@/components/atoms/icon-fi/IconFi.vue';
import { useAccordionItem } from '@/composables/accordionItem';

defineOptions({
  name: 'AccordionCustomizableFi',
});

const props = withDefaults(
  defineProps<{
    itemIndex?: number | null;
    id?: string;
    label?: string | null;
    color?: string | null;
    icon?: string;
    lazyContent?: boolean;
    multiple?: boolean;
    shopType?: string;
    ignoreOthers?: boolean;
    scrollTo?: boolean;
  }>(),
  {
    itemIndex: null,
    id: undefined,
    label: null,
    color: null,
    icon: 'chevron-down',
    lazyContent: false,
    multiple: false,
    shopType: 'default',
    ignoreOthers: false,
    scrollTo: false,
  },
);

/**
 * A prop default is evaluated once when the module loads, so the old `accordion_${Math.random()}`
 * gave every accordion on the page the same id - and a different one on the server than on the
 * client. useId is stable across both and unique per instance.
 */
const generatedId = useId();
const accordionId = computed(() => props.id || generatedId);

const emits = defineEmits<{
  'accordion-item-toggle': [value: boolean];
}>();

const { isOpen, toggleAccordion } = useAccordionItem(props, emits);

const rotate = computed(() => (isOpen.value ? '180' : '0'));

const toggleContent = () => {
  toggleAccordion();
};

defineExpose({
  isOpen,
  toggleAccordion,
});
</script>

<style src="./accordion-customizable-fi" />
